/**
 * HoverCards — Project, Challenge & Connection Hover Tooltips
 * ────────────────────────────────────────────────────────────
 * Extracted from CosmicMap. These HTML-based cards appear when hovering
 * over nodes or connection lines on the map, positioned relative to
 * the element's screen coords.
 *
 * Place at: src/components/map/HoverCards.jsx
 */

import React from 'react';

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Human-readable time gap between two ISO date strings.
 * e.g. "3 days later", "same day", "2 months later"
 */
function formatTimeGap(parentDate, childDate) {
  if (!parentDate || !childDate) return null;
  const parent = new Date(parentDate);
  const child = new Date(childDate);
  const diffMs = child - parent;
  const diffDays = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'same day';
  if (diffDays === 1) return '1 day later';
  if (diffDays < 7) return `${diffDays} days later`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks === 1) return '1 week later';
  if (diffWeeks < 5) return `${diffWeeks} weeks later`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return '1 month later';
  if (diffMonths < 12) return `${diffMonths} months later`;
  const diffYears = Math.floor(diffDays / 365);
  return diffYears === 1 ? '1 year later' : `${diffYears} years later`;
}

/**
 * Find shared tags between two tag arrays.
 * Tags can be strings or objects with .name
 */
function findSharedTags(tagsA, tagsB) {
  const normalize = (tags) =>
    (tags || []).map(t => (typeof t === 'string' ? t : t.name || '').toLowerCase().trim()).filter(Boolean);
  const setA = new Set(normalize(tagsA));
  const shared = normalize(tagsB).filter(t => setA.has(t));
  // Return original-case versions from tagsA
  const originals = (tagsA || []).map(t => typeof t === 'string' ? t : t.name || '').filter(Boolean);
  return [...new Set(shared.map(s => originals.find(o => o.toLowerCase() === s) || s))];
}


// ────────────────────────────────────────────────────────────────────────────
// Project Hover Card
// ────────────────────────────────────────────────────────────────────────────

export const ProjectHoverCard = ({ hoveredNode, containerSize }) => {
  const project = hoveredNode.node.project;
  const zone = hoveredNode.node.zone;
  const forks = project.forkCount || project.fork_count || 0;
  const starCount = project.starCount || project.star_count || 0;
  const coverImg = project.coverImage || project.cover_image || project.image_url;
  const tags = (project.tags || []).map(t => typeof t === 'string' ? t : t.name || '').filter(Boolean);

  const cardWidth = 260;
  const cardOffset = 20;
  const flipX = hoveredNode.screenX + cardWidth + cardOffset > containerSize.width;
  const flipY = hoveredNode.screenY + 200 > containerSize.height;

  return (
    <div
      className="absolute z-40 pointer-events-none"
      style={{
        left: flipX ? hoveredNode.screenX - cardWidth - cardOffset : hoveredNode.screenX + cardOffset,
        top: flipY ? hoveredNode.screenY - 160 : hoveredNode.screenY - 20,
        width: cardWidth,
        animation: 'hoverCardIn 0.15s ease-out',
      }}
    >
      <div className="bg-zinc-900/95 backdrop-blur-xl border border-zinc-700/50 rounded-xl shadow-2xl shadow-black/40 overflow-hidden">
        <div className="h-0.5" style={{ background: `linear-gradient(90deg, ${zone.color}, transparent)` }} />

        {coverImg && (
          <div className="h-28 overflow-hidden">
            <img src={coverImg} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border"
              style={{ color: zone.color, borderColor: zone.color + '40', backgroundColor: zone.color + '15' }}>
              {zone.shortLabel}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-white leading-tight mb-0.5 line-clamp-2">
            {project.name}
          </h4>
          {project.description && (
            <p className="text-[11px] text-zinc-400 leading-relaxed mb-2 line-clamp-2">{project.description}</p>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {tags.slice(0, 3).map((tag, i) => (
                <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700/50">{tag}</span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 text-[10px] text-zinc-500 pt-1.5 border-t border-zinc-800/60">
            {forks > 0 && (
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 3v12M18 3v6M6 21a3 3 0 100-6 3 3 0 000 6zM18 12a3 3 0 100-6 3 3 0 000 6z"/><path d="M18 9a9 9 0 01-9 9"/></svg>
                {forks} fork{forks !== 1 ? 's' : ''}
              </span>
            )}
            {starCount > 0 && (
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3 text-amber-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                {starCount}
              </span>
            )}
            <span className="ml-auto text-zinc-600 text-[9px]">
              double-click to open
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};


// ────────────────────────────────────────────────────────────────────────────
// Challenge Hover Card
// ────────────────────────────────────────────────────────────────────────────

export const ChallengeHoverCard = ({ hoveredNode, containerSize }) => {
  const challenge = hoveredNode.node.challenge;
  const title = challenge.challenge_title || challenge.challengeTitle || challenge.title || 'Challenge';
  const desc = challenge.challenge_description || challenge.challengeDescription || challenge.description || '';
  const type = challenge.challenge_type || challenge.challengeType || '';
  const responseCount = challenge.response_count || challenge.responseCount || 0;
  const skills = challenge.skills_needed || challenge.skillsNeeded || [];
  const author = challenge.requester?.username || challenge.author?.username || '';

  const cardWidth = 260;
  const cardOffset = 20;
  const flipX = hoveredNode.screenX + cardWidth + cardOffset > containerSize.width;
  const flipY = hoveredNode.screenY + 160 > containerSize.height;

  return (
    <div
      className="absolute z-40 pointer-events-none"
      style={{
        left: flipX ? hoveredNode.screenX - cardWidth - cardOffset : hoveredNode.screenX + cardOffset,
        top: flipY ? hoveredNode.screenY - 120 : hoveredNode.screenY - 20,
        width: cardWidth,
        animation: 'hoverCardIn 0.15s ease-out',
      }}
    >
      <div className="bg-zinc-900/95 backdrop-blur-xl border border-amber-500/30 rounded-xl shadow-2xl shadow-black/40 overflow-hidden">
        <div className="p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">
              {responseCount === 0 ? 'Open Challenge' : 'Challenge'}
            </span>
            {type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-400">
                {type.replace(/_/g, ' ')}
              </span>
            )}
          </div>
          <h4 className="text-sm font-semibold text-amber-200 leading-tight mb-0.5 line-clamp-2">
            &ldquo;{title}&rdquo;
          </h4>
          {author && <p className="text-[11px] text-zinc-500 mb-1.5">by @{author}</p>}
          {desc && <p className="text-[11px] text-zinc-400 leading-relaxed mb-2 line-clamp-2">{desc}</p>}
          {skills.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {skills.slice(0, 3).map((skill, i) => (
                <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">{skill}</span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 text-[10px] text-zinc-500 pt-1.5 border-t border-zinc-800/60">
            <span className="text-amber-400 font-medium">
              {responseCount === 0 ? 'No responses yet \u2014 be first!' : `${responseCount} response${responseCount !== 1 ? 's' : ''}`}
            </span>
            <span className="ml-auto text-zinc-600 text-[9px]">double-click to open</span>
          </div>
        </div>
      </div>
    </div>
  );
};


// ────────────────────────────────────────────────────────────────────────────
// Connection Hover Card
// ────────────────────────────────────────────────────────────────────────────
// Auto-generated descriptive tooltip for connection lines.
// Uses data already on both project nodes — no backend changes needed.
//
// Shows:  "Forked by @jordan · 3 days later"
//         Zone transition badge (cross-zone only)
//         Shared tags between parent and child
//         "click to trace lineage"

export const ConnectionHoverCard = ({ connection, screenX, screenY, containerSize }) => {
  const source = connection.sourceNode;
  const target = connection.targetNode;
  if (!source || !target) return null;

  // Determine parent vs child — the source (earlier node) is the parent
  const sourceProject = source.project || source.challenge;
  const targetProject = target.project || target.challenge;
  if (!sourceProject && !targetProject) return null;

  const isChallenge = source.type === 'challenge' || target.type === 'challenge';

  // For challenge connections, adapt the language
  const parentProject = sourceProject;
  const childProject = targetProject;

  const parentAuthor = parentProject?.author?.username;
  const childAuthor = childProject?.author?.username;
  const parentDate = parentProject?.created_at || parentProject?.createdAt;
  const childDate = childProject?.created_at || childProject?.createdAt;

  const timeGap = formatTimeGap(parentDate, childDate);

  const sourceZone = source.zone;
  const targetZone = target.zone;
  const isCrossZone = sourceZone && targetZone && sourceZone.id !== targetZone.id;

  const parentTags = parentProject?.tags || [];
  const childTags = childProject?.tags || [];
  const shared = findSharedTags(parentTags, childTags);

  // Build the headline sentence
  let headline;
  if (isChallenge) {
    const challengeNode = source.type === 'challenge' ? source : target;
    const challenge = challengeNode?.challenge;
    const title = challenge?.challenge_title || challenge?.challengeTitle || challenge?.title || 'a challenge';
    headline = `Response to "${title}"`;
  } else if (childAuthor && childAuthor !== parentAuthor) {
    headline = `Forked by @${childAuthor}`;
  } else if (childAuthor) {
    headline = `Forked by @${childAuthor}`;
  } else {
    headline = 'Fork connection';
  }

  // Card positioning (same pattern as node hover cards)
  const cardWidth = 240;
  const cardOffset = 15;
  const flipX = screenX + cardWidth + cardOffset > containerSize.width;
  const flipY = screenY + 120 > containerSize.height;

  return (
    <div
      className="absolute z-40 pointer-events-none"
      style={{
        left: flipX ? screenX - cardWidth - cardOffset : screenX + cardOffset,
        top: flipY ? screenY - 100 : screenY - 20,
        width: cardWidth,
        animation: 'hoverCardIn 0.15s ease-out',
      }}
    >
      <div className="bg-zinc-900/95 backdrop-blur-xl border border-zinc-700/50 rounded-xl shadow-2xl shadow-black/40 overflow-hidden">
        {/* Zone gradient bar — shows transition for cross-zone */}
        <div
          className="h-0.5"
          style={{
            background: isCrossZone
              ? `linear-gradient(90deg, ${sourceZone.color}, ${targetZone.color})`
              : `linear-gradient(90deg, ${sourceZone?.color || '#52525b'}, transparent)`,
          }}
        />

        <div className="p-3">
          {/* Headline */}
          <p className="text-[12px] font-medium text-white leading-snug mb-1">
            {headline}
          </p>

          {/* Time gap */}
          {timeGap && (
            <p className="text-[11px] text-zinc-500 mb-2">
              {timeGap}
            </p>
          )}

          {/* Zone transition badge (cross-zone only) */}
          {isCrossZone && (
            <div className="flex items-center gap-1.5 mb-2">
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border"
                style={{ color: sourceZone.color, borderColor: sourceZone.color + '40', backgroundColor: sourceZone.color + '15' }}
              >
                {sourceZone.shortLabel}
              </span>
              <svg className="w-3 h-3 text-zinc-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border"
                style={{ color: targetZone.color, borderColor: targetZone.color + '40', backgroundColor: targetZone.color + '15' }}
              >
                {targetZone.shortLabel}
              </span>
            </div>
          )}

          {/* Shared tags */}
          {shared.length > 0 && (
            <div className="mb-2">
              <p className="text-[9px] text-zinc-600 uppercase tracking-wider mb-1">Shared tags</p>
              <div className="flex flex-wrap gap-1">
                {shared.slice(0, 4).map((tag, i) => (
                  <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/20">
                    {tag}
                  </span>
                ))}
                {shared.length > 4 && (
                  <span className="text-[9px] px-1 py-0.5 text-zinc-600">+{shared.length - 4}</span>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center text-[9px] text-zinc-600 pt-1.5 border-t border-zinc-800/60">
            <span>click to trace lineage</span>
          </div>
        </div>
      </div>
    </div>
  );
};